import {
  Box,
  Typography,
  TextField,
  Paper,
  Button,
  CircularProgress,
  Slide,
  AppBar,
  Toolbar,
} from "@mui/material";
import { useState, useEffect } from "react";
import { getMissionVesion } from "../../services/api/getAll";
import {
  addMissionandvision,
  deleteMissionandvision,
} from "../../services/api/adminService";

const languages = ["en", "ar", "es"];

const defaultForm = {
  en: { mission: "", vision: "" },
  ar: { mission: "", vision: "" },
  es: { mission: "", vision: "" },
};

const MissionVisionForm = () => {
  const [form, setForm]: any = useState(defaultForm);
  const [initialForm, setInitialForm] = useState(defaultForm);
  const [isChanged, setIsChanged] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [id, setId] = useState("");
  useEffect(() => {
    const fetch = async () => {
      try {
        const response = await getMissionVesion();
        const translations = response?.items?.[0]?.translations || [];
        setId(response?.items?.[0]?.id);
        const loaded: any = { ...defaultForm };
        translations.forEach(({ languageCode, mission, vision }: any) => {
          loaded[languageCode] = { mission, vision };
        });
        setForm(loaded);
        setInitialForm(loaded);
      } catch (e) {
        console.error("Error loading mission & vision", e);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  useEffect(() => {
    setIsChanged(JSON.stringify(form) !== JSON.stringify(initialForm));
  }, [form, initialForm]);

  const handleChange = (lang: string, field: string, value: string) => {
    setForm((prev: any) => ({
      ...prev,
      [lang]: {
        ...prev[lang],
        [field]: value,
      },
    }));
  };

  const handleSave = async () => {
    const translations = languages.map((lang) => ({
      languageCode: lang,
      mission: form[lang].mission,
      vision: form[lang].vision,
    }));

    try {
      setSaving(true);
      await addMissionandvision({ translations });
      setInitialForm(form);
      setIsChanged(false);
    } catch (error) {
      console.error("Failed to save Mission & Vision content:", error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box
        height="80vh"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <CircularProgress size={50} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        p: { xs: 2, md: 5 },
        bgcolor: "#f4f7fa",
        fontFamily: "Poppins",
        background: "linear-gradient(135deg, #f9f9fb, #e6eef6)",
      }}
    >
      <Slide direction="down" in={isChanged}>
        <AppBar
          elevation={3}
          color="default"
          sx={{
            background: "rgba(255,255,255,0.9)",
            backdropFilter: "blur(12px)",
            borderBottom: "1px solid #e0e0e0",
          }}
        >
          <Toolbar sx={{ justifyContent: "space-between", px: 4, py: 1.5 }}>
            <Typography
              variant="subtitle1"
              fontWeight="bold"
              sx={{ color: "#333" }}
            >
              Unsaved Changes
            </Typography>
            <Button
              onClick={handleSave}
              variant="contained"
              size="medium"
              disabled={saving}
              sx={{
                background: "#007aff",
                borderRadius: 2,
                px: 4,
                fontWeight: "bold",
                textTransform: "none",
                "&:hover": {
                  backgroundColor: "#005bb5",
                },
              }}
            >
              {saving ? (
                <CircularProgress size={20} sx={{ color: "#fff" }} />
              ) : (
                "Save"
              )}
            </Button>
          </Toolbar>
        </AppBar>
      </Slide>

      <Box sx={{ mt: isChanged ? 10 : 2, mb: 5 }}>
        <Typography variant="h3" fontWeight="bold" color="#1a1a1a">
          Mission & Vision
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          Manage your mission and vision across multiple languages
        </Typography>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          gap: 4,
        }}
      >
        {languages.map((lang) => (
          <Paper
            key={lang}
            elevation={8}
            sx={{
              p: 4,
              borderRadius: "20px",
              background: "#ffffffee",
              border: "1px solid #e0e0e0",
              boxShadow: "0 10px 30px rgba(0, 0, 0, 0.06)",
              display: "flex",
              flexDirection: "column",
              gap: 3,
            }}
          >
            <Typography
              variant="h6"
              fontWeight="600"
              color="primary"
              sx={{ letterSpacing: 1, textTransform: "uppercase" }}
            >
              {lang}
            </Typography>

            <TextField
              label={`Mission (${lang.toUpperCase()})`}
              multiline
              fullWidth
              minRows={5}
              value={form[lang].mission}
              onChange={(e) => handleChange(lang, "mission", e.target.value)}
              sx={{
                fontFamily: "Poppins",
                background: "#fafafa",
                borderRadius: 2,
                "& .MuiOutlinedInput-root": {
                  color: "#000",
                  "& textarea": {
                    color: "#000",
                    fontFamily: "Poppins",
                  },
                },
              }}
              InputLabelProps={{
                sx: {
                  color: "#444",
                  fontFamily: "Poppins",
                },
              }}
            />

            <TextField
              label={`Vision (${lang.toUpperCase()})`}
              multiline
              fullWidth
              minRows={5}
              value={form[lang].vision}
              onChange={(e) => handleChange(lang, "vision", e.target.value)}
              sx={{
                fontFamily: "Poppins",
                background: "#fafafa",
                borderRadius: 2,
                "& .MuiOutlinedInput-root": {
                  color: "#000",
                  "& textarea": {
                    color: "#000",
                    fontFamily: "Poppins",
                  },
                },
              }}
              InputLabelProps={{
                sx: {
                  color: "#444",
                  fontFamily: "Poppins",
                },
              }}
            />
          </Paper>
        ))}
      </Box>
    </Box>
  );
};

export default MissionVisionForm;
